import { memo, useState, forwardRef, useImperativeHandle, useEffect, useMemo, useRef } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import Colon from '@lcsc-ui/components/Colon';
import CustomCheckBox from '@lcsc-ui/components/CustomCheckBox';
import { Input } from 'antd';
import { cloneDeep } from 'lodash-es';
import { styled } from 'styled-components';

import SvgIcon from '@/components/SvgIcon';

const ScrollbarCustomDiv = styled.div`
  .so-filter-condition-scroll {
    scrollbar-width: thin;
  }

  .so-filter-condition-scroll::-webkit-scrollbar {
    width: 5px;
    display: none;
  }

  .so-filter-condition-scroll::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.45);
    border-radius: 19px;
  }

  .so-filter-condition-scroll::-webkit-scrollbar-thumb {
    border-radius: 19px;
  }

  .so-filter-condition-scroll::-webkit-scrollbar-button {
    display: none;
  }
`;

const maxLength = 20; // 初始状态下 为了性能考虑 最多展示20个 不需要展示所有

const Index = forwardRef((props, ref) => {
  const { hasCheckAll = true } = props;
  const keyState = props?.data?.value || 'id';
  const labelState = props?.data?.label || 'label';
  const [showMore, setShowMore] = useState(false);
  const [multipleSelect, setMultipleSelect] = useState(false);
  const [selectDataList, setSelectDataList] = useState([]);
  const [checkboxSelectDataList, setCheckboxSelectDataList] = useState([]);
  const [activeLetter, setActiveLetter] = useState('全部');
  const [inputValue, setInputValue] = useState('');
  const [hasMoreBtnAuth, setHasMoreBtnAuth] = useState(false);
  const parentNeedParam = props?.data?.parameterCode || props?.data?.name;
  const elementRef = useRef(null);

  useEffect(() => {
    let selectDataListState = [];
    if (props?.selectValue || Object.keys(props?.selectValue)?.length > 0) {
      if (!props?.data?.parameterCode) {
        props?.selectValue.split('$').forEach((item) => {
          const obj = props?.data?.list?.find((row) => row[keyState] === item);
          if (obj) {
            selectDataListState.push(obj);
          }
        });
      } else {
        selectDataListState = props?.selectValue[props?.data?.parameterCode] || [];
      }
    }
    setSelectDataList(selectDataListState);
  }, [props?.selectValue, props?.data?.list, keyState, props?.data?.parameterCode]);

  // 数据发生变化 或者展开伸缩发生变化 需要重置选中的字母索引以及输入框内容
  useEffect(() => {
    setActiveLetter('全部');
    setInputValue('');
  }, [showMore, props?.data?.list]);

  // 增加展开按钮显示逻辑 超过一行才显示展开按钮
  useEffect(() => {
    setTimeout(() => {
      const height = elementRef.current?.scrollHeight;
      setHasMoreBtnAuth(height > 30);
    }, 0);
  }, [props?.data?.list, multipleSelect, selectDataList]);

  const handleSelectItem = (item) => {
    if (props?.disabled) return;
    setShowMore(false);
    props?.changeSendParams([...selectDataList, item], parentNeedParam);
  };

  const deleteSelectItem = (item) => {
    const selectDataListState = cloneDeep(selectDataList);
    const index = selectDataListState.findIndex((row) => row[keyState] === item[keyState]);
    selectDataListState.splice(index, 1);
    props?.changeSendParams(selectDataListState, parentNeedParam);
  };

  const openShowMore = () => {
    if (props?.disabled) return;
    setShowMore(!showMore);
    props?.onlyOpenOneShowMore(parentNeedParam, !showMore); // 仅打开一个展开状态 其他都关闭
  };

  const handleMultiple = () => {
    if (props?.disabled) return;
    if (selectDataList.length > 0) {
      setCheckboxSelectDataList(selectDataList);
    }
    setShowMore(true);
    changeMultipleSelect(true);
    props?.onlyOpenOneShowMore(parentNeedParam, !showMore); // 仅打开一个展开状态 其他都关闭
  };

  const changeMultipleSelect = (value) => {
    setMultipleSelect(value);
  };

  const handleFilterLetter = (item) => {
    setActiveLetter(item);
  };

  const changeCheckbox = (item) => {
    const checkboxSelectDataListState = cloneDeep(checkboxSelectDataList);
    if (checkboxSelectDataListState.map((item) => item[keyState]).includes(item[keyState])) {
      const index = checkboxSelectDataListState.findIndex((row) => row[keyState] === item[keyState]);
      checkboxSelectDataListState.splice(index, 1);
    } else {
      checkboxSelectDataListState.push(item);
    }
    setCheckboxSelectDataList(checkboxSelectDataListState);
  };

  const handleCancelMultiple = () => {
    setShowMore(false);
    setCheckboxSelectDataList([]);
    changeMultipleSelect(false);
  };

  const changeCouponValue = (e, value) => {
    props?.changeSendCouponParams(e.target.checked ? value : '');
    setCheckboxSelectDataList([]);
    setShowMore(false);
    changeMultipleSelect(false);
  };

  const handleConfirmMultiple = () => {
    if (
      typeof props?.checkSelectNum === 'function' &&
      !props?.checkSelectNum(checkboxSelectDataList, parentNeedParam)
    ) {
      return false;
    }
    setShowMore(false);
    changeMultipleSelect(false);
    const checkboxSelectDataListState = cloneDeep(checkboxSelectDataList);
    if (checkboxSelectDataListState.length) {
      props?.changeSendParams(checkboxSelectDataListState, parentNeedParam);
      setCheckboxSelectDataList([]);
    }
  };

  const changeInputValue = (e) => {
    setInputValue(e.target.value);
  };

  const clearAll = () => {
    props?.changeSendParams([], parentNeedParam);
  };

  const resetInitStatus = () => {
    setShowMore(false);
    setMultipleSelect(false);
    setCheckboxSelectDataList([]);
  };

  useImperativeHandle(ref, () => ({
    resetInitStatus,
  }));

  const selectAll = (e) => {
    e.stopPropagation();
    const checkboxSelectDataListState = cloneDeep(checkboxSelectDataList);
    if (checkboxSelectDataListState.length === 0) {
      setCheckboxSelectDataList(showList);
    } else {
      const newArr = [];
      if (
        showList.every((item) => checkboxSelectDataListState.map((item) => item[keyState]).includes(item[keyState]))
      ) {
        checkboxSelectDataListState.forEach((item) => {
          if (!showList.some((row) => row[keyState] === item[keyState])) {
            newArr.push(item);
          }
        });
        setCheckboxSelectDataList(newArr);
      } else {
        checkboxSelectDataListState.forEach((item) => {
          if (!showList.some((row) => row[keyState] === item[keyState])) {
            newArr.push(item);
          }
        });
        setCheckboxSelectDataList([...newArr, ...showList]);
      }
    }
  };

  // 渲染条件筛选
  const renderSearchCondition = () => {
    return (
      <>
        {props?.data?.letterList?.length > 0 && (
          <div className={'flex flex-wrap flex-1'}>
            {['全部', ...(props?.data?.letterList || [])].map((item) => {
              return (
                <div
                  className={`mr-[16px] flex h-[22px] cursor-pointer items-center text-[14px] hover:text-[#0093E6] ${activeLetter === item ? 'font-bold text-[#0093E6]' : 'text-[#444444]'}`}
                  key={item}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterLetter(item);
                  }}
                >
                  {item}
                </div>
              );
            })}
          </div>
        )}

        {!props?.data?.letterList?.length && !props?.data?.showInputCondition && (
          <div className={'mb-[9px] flex flex-wrap'}>
            <div className={'h-[22px]'}></div>
          </div>
        )}
        {props?.data?.showInputCondition && (showMore || multipleSelect) && (
          <Input
            className={'!mb-[10px] !h-[24px] !w-[170px] !text-[14px]'}
            placeholder={'搜索选项'}
            onChange={(e) => changeInputValue(e)}
            onCompositionStart={() => {
              typeof props?.changeComposition === 'function' && props?.changeComposition(true);
            }}
            onCompositionEnd={() => {
              typeof props?.changeComposition === 'function' && props?.changeComposition(false);
            }}
            value={inputValue}
          />
        )}
      </>
    );
  };

  const showList = useMemo(() => {
    const len = showMore ? props?.data?.list.length : maxLength;
    if (!props?.data?.list) return [];
    let list = props?.data?.list.slice(0, len);
    if (props?.data?.letterList?.length > 0 && activeLetter !== '全部') {
      list = list.filter((item) => item?.firstLetter === activeLetter);
    } else if (props?.data?.showInputCondition && inputValue) {
      list = list.filter((item) => item[labelState].toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
    }
    return list;
  }, [props?.data?.list, activeLetter, inputValue, labelState, showMore]);

  const renderDataList = () => {
    return (
      <>
        {multipleSelect
          ? showList.map((item, index) => {
              return (
                <li
                  style={{
                    width: 'calc(12.5% - 40px)',
                    boxSizing: 'border-box',
                  }}
                  className={`mr-[40px] text-[12px] box-border h-[22px] ${showMore ? 'mb-[4px]' : ''}`}
                  key={index}
                  title={item[labelState]}
                >
                  <CustomCheckBox
                    value={item[keyState]}
                    checked={checkboxSelectDataList.map((item) => item[keyState]).includes(item[keyState])}
                    onChange={() => changeCheckbox(item)}
                  >
                    <div className={'max-w-[102px] truncate  text-[12px] text-[#444444] hover:text-[#0093E6]'}>
                      {item[labelState]}
                    </div>
                  </CustomCheckBox>
                </li>
              );
            })
          : selectDataList.length === 0
            ? showList.map((item, index) => {
                return (
                  <li
                    key={index}
                    className={`mr-[40px] flex h-[22px] items-center text-[#444444] ${props?.disabled ? '' : 'hover:text-[#0093E6] cursor-pointer'} ${showMore ? 'mb-[4px] text-[12px]' : 'text-[14px]'}`}
                    style={{
                      width: showMore ? 'calc(12.5% - 40px)' : 'auto',
                      boxSizing: 'border-box',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectItem(item);
                    }}
                    title={item[labelState]}
                  >
                    <div className={'max-w-[118px] truncate'}>{item[labelState]}</div>
                  </li>
                );
              })
            : selectDataList.map((item, index) => {
                return (
                  <li
                    key={index}
                    className={`relative mb-[10px] text-[14px] mr-[15px] flex h-[22px] items-center rounded-[3px] border border-solid border-[#0093E6] pl-[5px] pr-[20px] text-[#0093E6]`}
                    title={item[labelState]}
                  >
                    <div className={'max-w-[160px] truncate'}>{item[labelState]}</div>
                    <div
                      className={'absolute right-0 top-0  h-[22px] w-[11px] cursor-pointer'}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSelectItem(item);
                      }}
                    >
                      <SvgIcon app="so" iconName="so-delete" className={'size-[11px]'} />
                    </div>
                  </li>
                );
              })}
      </>
    );
  };

  // 多选按钮渲染
  const renderMultipleBtn = () => {
    return (
      <div className={'mb-[10px] flex justify-center rounded-bl-[5px] rounded-br-[5px] bg-[#F7F7F7] py-[10px]'}>
        <button
          className={
            'mr-[10px] flex h-[26px] w-[55px] items-center justify-center rounded-[4px] border border-solid border-[rgba(0,0,0,0.15)]  bg-white hover:border-[#0093E6] hover:text-[#0093E6]'
          }
          onClick={(e) => {
            e.stopPropagation();
            handleCancelMultiple();
          }}
        >
          取消
        </button>
        <button
          className={
            'flex h-[26px] w-[55px] items-center justify-center rounded-[4px] bg-[#0093E6]  text-[#FFFFFF] hover:opacity-80'
          }
          onClick={(e) => {
            e.stopPropagation();
            handleConfirmMultiple();
          }}
        >
          确定
        </button>
      </div>
    );
  };

  const [isHoverStatus, setIsHoverStatus] = useState(false);
  const ArrowIcon = useMemo(() => {
    if (isHoverStatus === true) {
      return 'so-arrow-active';
    } else {
      return 'so-arrow';
    }
  }, [isHoverStatus]);

  // 右侧按钮渲染
  const renderRightBtn = () => {
    return (
      <div className={`${selectDataList.length > 0 ? '' : 'items-center'} flex`}>
        {props?.data?.showCouponCheckbox && showMore && (
          <>
            <CustomCheckBox checked={props?.couponValue === -2} onChange={(e) => changeCouponValue(e, -2)}>
              <span className={'text-[14px]'}>全部元器件</span>
            </CustomCheckBox>
            <div className={'mx-[13px]'}>
              <CustomCheckBox checked={props?.couponValue === -1} onChange={(e) => changeCouponValue(e, -1)}>
                <span className={'text-[14px]'}>全部工业品</span>
              </CustomCheckBox>
            </div>
          </>
        )}
        {selectDataList.length === 0 && hasMoreBtnAuth && (
          <div
            className={`flex h-[22px] items-center justify-center ${props?.disabled ? '' : 'cursor-pointer  hover:text-[#0093E6] '}`}
            onClick={(e) => {
              e.stopPropagation();
              openShowMore();
            }}
            onMouseEnter={() => {
              if (props?.disabled) return;
              setIsHoverStatus(true);
            }}
            onMouseLeave={() => setIsHoverStatus(false)}
          >
            <div className={'pr-[2px]'}>{showMore ? '收起' : '展开'}</div>
            <SvgIcon
              app="so"
              iconName={ArrowIcon}
              className={`h-[6px] w-[9px] ${showMore ? 'rotate-180 transform ' : ''}`}
            />
          </div>
        )}
        {props?.data?.list?.length > 1 && !multipleSelect && selectDataList.length === 0 && (
          <div
            className={`ml-[13px] flex h-[22px] w-[42px] items-center justify-center rounded-[4px] border border-solid border-[#D9D9D9] ${props?.disabled ? '' : 'hover:border-[#0093E6] hover:text-[#0093E6] cursor-pointer'}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMultiple();
            }}
          >
            多选
          </div>
        )}
        {selectDataList.length > 0 && (
          <div
            className={
              'flex h-[22px] w-[45px] cursor-pointer  items-center justify-center rounded-[4px] hover:border-[#0093E6] hover:text-[#0093E6]'
            }
            onClick={(e) => {
              e.stopPropagation();
              clearAll(true);
            }}
          >
            <span className={'pr-[2px]'}>清除</span>
            <CloseOutlined />
          </div>
        )}
        {multipleSelect && hasCheckAll && (
          <div className={'hover:text-[#0093E6] cursor-pointer translate-y-[2px]'} onClick={(e) => selectAll(e)}>
            全选
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`flex items-start justify-center ${props?.wrapCls || ''}`}>
        <div className={'flex h-[22px] items-center justify-center text-[14px] text-[#69788A] mr-[6px]'}>
          {props?.data?.name}
          <Colon />
        </div>
        {!showMore ? (
          <div className={`${selectDataList.length === 0 ? 'mb-[10px]' : ''} flex flex-1`}>
            <div className={'flex flex-1 flex-col'}>
              {showMore && renderSearchCondition()}
              <ul
                className={`flex flex-wrap ${showMore || selectDataList.length > 0 ? '' : 'overflow-hidden h-[22px]'}`}
                ref={elementRef}
              >
                {renderDataList()}
              </ul>
            </div>
            <div className={'flex text-[14px] text-[#54657D]'}>{renderRightBtn()}</div>
          </div>
        ) : (
          <div className={'mb-[10px] flex flex-1 flex-col'}>
            <div className={'flex flex-1 justify-between'}>
              {showMore && renderSearchCondition()}
              <div className={'flex text-[14px] text-[#54657D]'}>{renderRightBtn()}</div>
            </div>
          </div>
        )}
      </div>
      {showMore && (
        <ScrollbarCustomDiv>
          <ul
            className={`so-filter-condition-scroll ${!multipleSelect ? 'mb-[10px] rounded-[5px]' : 'rounded-tl-[5px] rounded-tr-[5px]'} flex max-h-[142px] flex-wrap overflow-auto bg-[#F7F7F7] px-[40px] py-[10px]`}
            style={{
              overscrollBehavior: 'contain',
            }}
          >
            {renderDataList()}
          </ul>
        </ScrollbarCustomDiv>
      )}
      {multipleSelect && renderMultipleBtn()}
    </>
  );
});

Index.displayName = 'Index';

export default memo(Index);
